import { useEffect } from 'react';
import cn from 'classnames';
import IconWrapper from '@components/ui/Icons';
import { useTranslation } from 'next-i18next';
import { usePasswordValidation } from '../../../hooks/usePasswordValidation';
import styles from './PasswordValidation.module.scss';

interface PasswordProps {
  password: {
    firstPassword: string;
    secondPassword: string;
  };
  needsMatch: boolean;
  setNewPasswordIsValid?: (
    value: boolean | ((setNewPasswordIsValid: boolean) => boolean)
  ) => void | undefined;
  classNames?: string | string[];
}

export default function PasswordValidation(props: PasswordProps) {
  const { password, needsMatch, setNewPasswordIsValid, classNames } = props;
  const { t } = useTranslation('common');
  const [validLength, hasNumber, upperCase, lowerCase, match] =
    usePasswordValidation({
      firstPassword: password.firstPassword,
      secondPassword: password.secondPassword,
    });

  const passwordRules = [
    { id: 1, passed: lowerCase, text: t('password.validation.rule.lowercase') },
    { id: 2, passed: upperCase, text: t('password.validation.rule.uppercase') },
    { id: 3, passed: hasNumber, text: t('password.validation.rule.hasNumber') },
    {
      id: 4,
      passed: validLength,
      text: t('password.validation.rule.validLength'),
    },
  ];

  if (needsMatch) {
    passwordRules.push({
      id: 5,
      passed: match,
      text: t('password.validation.rule.match'),
    });
  }

  useEffect(() => {
    if (setNewPasswordIsValid) {
      if (lowerCase && upperCase && hasNumber && validLength && !needsMatch) {
        setNewPasswordIsValid(true);
      } else if (
        lowerCase &&
        upperCase &&
        hasNumber &&
        validLength &&
        needsMatch &&
        match
      ) {
        setNewPasswordIsValid(true);
      } else {
        setNewPasswordIsValid(false);
      }
    }
  });

  return (
    <div className={cn(styles.passwordValidationContainer, classNames)}>
      <div className={styles.headerText}>{t('password.validation.header')}</div>
      {passwordRules.map(({ id, passed, text }) => (
        <div key={`password-rule-${id}`} className={styles.passwordRow}>
          <IconWrapper
            icon={passed ? 'checkmark-green' : 'close'}
            height={13}
            width={13}
            fill={
              passed
                ? 'var(--color-alert-success-icon)'
                : 'var(--color-gray-darker)'
            }
          />
          <div className={styles.passwordText}>{text}</div>
        </div>
      ))}
    </div>
  );
}
